import { Link, useLocation } from "react-router-dom";
import SiteChrome from "@/components/candidate/SiteChrome";
import {
    CheckCircle2,
    Home,
    User,
    Phone,
    Briefcase,
    Calendar,
    Sparkles,
} from "lucide-react";

export default function ThankYou() {
    const location = useLocation();
    const candidate = location.state?.candidate;

    return (
        <SiteChrome eyebrow="Attendance Confirmed">
            <div
                data-testid="thank-you-page"
                className="max-w-3xl mx-auto px-6 md:px-10 py-14 md:py-20"
            >
                {/* Hero */}
                <div className="relative overflow-hidden bg-white border border-[#0F1F38]/10 shadow-[0_1px_0_rgba(15,31,56,0.02),0_28px_60px_-30px_rgba(15,31,56,0.25)]">
                    {/* Decorative band */}
                    <div className="absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-emerald-50/60 to-transparent pointer-events-none" />
                    <div className="absolute -top-20 -right-20 w-56 h-56 rounded-full bg-emerald-100/50 blur-3xl pointer-events-none" />

                    <div className="relative px-6 md:px-12 pt-10 md:pt-14 pb-8 md:pb-10 text-center">
                        <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-white border border-emerald-200 shadow-sm mb-6">
                            <CheckCircle2
                                size={40}
                                strokeWidth={1.4}
                                className="text-emerald-600"
                            />
                        </div>
                        <div className="text-[10px] tracking-label uppercase text-emerald-700/80 mb-2">
                            <Sparkles size={11} className="inline mr-1" />
                            Attendance confirmed
                        </div>
                        <h1
                            data-testid="thank-you-heading"
                            className="font-display text-5xl md:text-6xl leading-[0.95] text-[#0F1F38] mb-4"
                        >
                            Thank&nbsp;you!
                        </h1>
                        <p className="text-base md:text-lg text-[#4B5563] leading-relaxed max-w-xl mx-auto">
                            Your interview attendance has been successfully marked.
                            Our HR team will proceed with the further recruitment
                            process.
                        </p>
                    </div>

                    {candidate && (
                        <div
                            data-testid="thank-you-details"
                            className="relative border-t border-[#0F1F38]/10 grid grid-cols-1 sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-[#0F1F38]/10"
                        >
                            <SummaryRow
                                icon={<User size={14} strokeWidth={1.5} />}
                                label="Candidate"
                                value={candidate.name}
                                sub={candidate.email}
                            />
                            <SummaryRow
                                icon={<Phone size={14} strokeWidth={1.5} />}
                                label="Phone"
                                value={candidate.phone}
                                sub={candidate.role}
                            />
                            <SummaryRow
                                icon={<Briefcase size={14} strokeWidth={1.5} />}
                                label="Role"
                                value={candidate.role}
                                sub={
                                    candidate.college_name
                                        ? candidate.college_name
                                        : undefined
                                }
                            />
                            <SummaryRow
                                icon={<Calendar size={14} strokeWidth={1.5} />}
                                label="Scheduled"
                                value={`${candidate.schedule_date || "—"}`}
                                sub={candidate.schedule_time}
                            />
                        </div>
                    )}

                    <div className="relative px-6 md:px-12 py-6 flex flex-col md:flex-row items-center justify-between gap-4 bg-[#F7F5F0]/40 border-t border-[#0F1F38]/10">
                        <div className="text-xs text-[#4B5563] text-center md:text-left">
                            Verified on{" "}
                            <strong>
                                {candidate?.attendance_verified_date || "—"}
                            </strong>{" "}
                            at{" "}
                            <strong>
                                {candidate?.attendance_verified_time || "—"}
                            </strong>
                        </div>
                        <Link
                            to="/"
                            data-testid="btn-back-home"
                            className="inline-flex items-center gap-2 px-5 py-2.5 text-xs tracking-label uppercase font-semibold bg-[#0F1F38] text-[#F7F5F0] hover:bg-[#1A3254] transition-colors"
                        >
                            <Home size={13} strokeWidth={1.6} />
                            Back to Careers
                        </Link>
                    </div>
                </div>

                <p className="text-center text-xs text-[#0F1F38]/50 mt-8">
                    Need help? Reach out to your BluBridge HR contact.
                </p>
            </div>
        </SiteChrome>
    );
}

function SummaryRow({ icon, label, value, sub }) {
    return (
        <div className="px-6 md:px-8 py-5 flex items-start gap-3">
            <div className="mt-0.5 w-8 h-8 rounded-full bg-[#F7F5F0] border border-[#0F1F38]/10 flex items-center justify-center text-[#0F1F38]/70 shrink-0">
                {icon}
            </div>
            <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-label text-[#0F1F38]/50">
                    {label}
                </div>
                <div className="text-sm md:text-base font-medium text-[#0F1F38] truncate">
                    {value || "—"}
                </div>
                {sub && (
                    <div className="text-xs text-[#4B5563] truncate">{sub}</div>
                )}
            </div>
        </div>
    );
}
